import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useGTM from '../hooks/useGTM';
import {
  IoLogoGithub,
  IoLogoLinkedin,
  IoGlobe,
  IoLocationSharp,
  IoMail,
  IoPaperPlane,
  IoCloudDownload,
} from 'react-icons/io5';
import '../static/css/Aside.css';

function Aside() {
  const { i18n } = useTranslation();
  const { trackLanguageChange, trackDownload, trackClick } = useGTM();
  const [activeLang, setActiveLang] = useState(i18n.language || 'pt');
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLanguageChange = (lang) => {
    const current = i18n.language;
    setActiveLang(lang);
    i18n.changeLanguage(lang);
    if (lang === 'unown') {
      document.body.classList.add('unown-font');
    } else {
      document.body.classList.remove('unown-font');
    }
    trackLanguageChange(current, lang);
  };

  const downloadCV = () => {
    const link = document.createElement('a');
    link.href = '/Vanilson_Souza_CV.pdf';
    link.download = 'Vanilson_Souza_CV.pdf';
    link.click();
    trackDownload('Vanilson_Souza_CV.pdf', 'pdf');
  };

  const handleSocialClick = (platform, url) => {
    trackClick(platform, 'social-link', url);
  };

  const toggleMenu = () => {
    setMenuOpen((current) => {
      trackClick('menu-mobile', 'button', current ? 'close' : 'open');
      return !current;
    });
  };

  return (
    <>
      <motion.div
        className="language-select"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
        role="region"
        aria-label="Seletor de idioma"
      >
        <div className="lang-toggle-pill">
          <button
            className={`lang-toggle-btn${activeLang === 'pt' ? ' lang-active' : ''}`}
            onClick={() => handleLanguageChange('pt')}
            aria-label="Português"
          >
            <img
              src="/img/fotos/bandeira-PT.png"
              alt="Bandeira do Brasil"
              className="lang-icon"
              loading="lazy"
              decoding="async"
            />
            <span>PT</span>
          </button>
          <button
            className={`lang-toggle-btn${activeLang === 'en' ? ' lang-active' : ''}`}
            onClick={() => handleLanguageChange('en')}
            aria-label="English"
          >
            <img
              src="/img/fotos/bandeira-EN.png"
              alt="United States flag"
              className="lang-icon"
              loading="lazy"
              decoding="async"
            />
            <span>EN</span>
          </button>
        </div>
      </motion.div>

      <button
        className="menu-toggle"
        onClick={toggleMenu}
        aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
        aria-expanded={menuOpen}
      >
        <div className={`hamburger ${menuOpen ? 'open' : ''}`}>
          <span />
          <span />
          <span />
        </div>
      </button>

      <motion.aside
        className={menuOpen ? 'open' : ''}
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
        role="complementary"
        aria-label="Informações de perfil e contato"
      >
        <motion.div
          className="profile-image"
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 300 }}
          role="img"
          aria-label="Foto de perfil de Vanilson Souza"
        >
          <img
            src="https://avatars.githubusercontent.com/u/136197396?s=400&u=ab9778936b860a01a5d15c54b7d681fb70f86ab1&v=4"
            alt="Imagem de perfil"
            loading="lazy"
            decoding="async"
          />
        </motion.div>

        <motion.div
          className="profile-info"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          role="region"
          aria-label="Informações básicas"
        >
          <h1>Vanilson</h1>
          <p>Souza</p>
        </motion.div>

        <motion.div
          className="contact-info"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          role="region"
          aria-label="Informações de contato"
        >
          <motion.a
            href="https://wa.me/5582982300284"
            target="_blank"
            rel="noreferrer noopener"
            whileHover={{ scale: 1.05, x: 5 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleSocialClick('whatsapp', 'https://wa.me/5582982300284')}
            aria-label="WhatsApp"
          >
            <IoPaperPlane className="icon" aria-hidden="true" />
            <span>Vanilson Souza</span>
          </motion.a>
          <motion.a
            href="https://maps.app.goo.gl/dPDCPwXJmQp22F3T8"
            target="_blank"
            rel="noreferrer noopener"
            whileHover={{ scale: 1.05, x: 5 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleSocialClick('location', 'maps')}
            aria-label="Localização"
          >
            <IoLocationSharp className="icon" aria-hidden="true" />
            <span>Canapi-AL</span>
          </motion.a>
          <motion.a
            href="mailto:vanilsonvns75@gmail.com"
            target="_blank"
            rel="noreferrer noopener"
            whileHover={{ scale: 1.05, x: 5 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleSocialClick('email', 'mailto')}
            aria-label="Email"
          >
            <IoMail className="icon" aria-hidden="true" />
            <span>vanilsonvns75@gmail.com</span>
          </motion.a>
        </motion.div>

        <motion.div
          className="social-links"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          role="region"
          aria-label="Redes sociais"
        >
          <motion.a
            href="https://github.com/vanilson05"
            target="_blank"
            rel="noreferrer noopener"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleSocialClick('github', 'https://github.com/vanilson05')}
            aria-label="GitHub"
          >
            <IoLogoGithub className="icon" aria-hidden="true" />
          </motion.a>
          <motion.a
            href="https://www.linkedin.com/in/vanilson~silva/"
            target="_blank"
            rel="noreferrer noopener"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() =>
              handleSocialClick('linkedin', 'https://www.linkedin.com/in/vanilson~silva/')
            }
            aria-label="LinkedIn"
          >
            <IoLogoLinkedin className="icon" aria-hidden="true" />
          </motion.a>
          <motion.a
            href="#"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleSocialClick('portfolio', '#')}
            aria-label="Portfólio"
          >
            <IoGlobe className="icon" aria-hidden="true" />
          </motion.a>
        </motion.div>

        <motion.button
          className="cv-button"
          onClick={downloadCV}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Download do currículo"
        >
          <IoCloudDownload className="icon" aria-hidden="true" />
          Download CV
        </motion.button>
      </motion.aside>
    </>
  );
}

export default Aside;
